/* eslint-disable max-lines-per-function, max-lines, max-len */
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Menu } from 'primereact/menu';
import { useNavigate } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { getActivityReportHolidayListRequest } from '../../../Api/request/activityReport';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './EmployeeUtilizationReport.scss';
import { useAuth } from '../../../Hooks/useAuth';
import moreIcon from '../../../Assets/images/svg/dar_more.svg';

const PAGE_SIZE = 10;
const TABLE_SCROLL_RATIO = 0.7;
const FILTER_URL_BASE = '/v1/employee-utilization-report';
const PROJECT_SERVICE_NAME = serviceName.PROJECT_MS;
const UTILIZATION_ACTIVITY_REPORT_ROUTE = '/mis/utilization-activity-report';
const PROJECT_DETAILS_ROUTE = '/delivery/project-details';
const DAILY_CELL_STATE_CLASS_MAP = {
  fullDayLeave: 'employee-utilization-report-day-cell-full-day-leave',
  halfDayLeave: 'employee-utilization-report-day-cell-half-day-leave',
  unloggedExcess: 'employee-utilization-report-day-cell-unlogged-excess',
};
const DAILY_CELL_STATE_CLASSES = Object.values(DAILY_CELL_STATE_CLASS_MAP);
const DAILY_EFFORT_LEGEND_ITEMS = [
  {
    label: 'Full day Leave',
    swatchClassName: 'employee-utilization-report-legend-dot-full-day-leave',
  },
  {
    label: 'Half Day Leave',
    swatchClassName: 'employee-utilization-report-legend-dot-half-day-leave',
  },
  {
    label: 'Unlogged / Excess Logged',
    swatchClassName: 'employee-utilization-report-legend-dot-unlogged-excess',
  },
];

const getInitialTableScrollHeight = () => {
  if (typeof window === 'undefined') {
    return '400px';
  }

  return `${Math.floor(window.innerHeight * TABLE_SCROLL_RATIO)}px`;
};

const STATIC_COLUMNS = [
  { key: 'employeeName', label: 'Employee Name', numeric: false },
  { key: 'employeeId', label: 'Employee ID', numeric: false },
  { key: 'employeeVertical', label: 'Employee Vertical', numeric: false },
  { key: 'employeeType', label: 'Employee Type', numeric: false },
  { key: 'workingDays', label: 'Working days', numeric: true },
  { key: 'leaveDays', label: 'Leave (Days)', numeric: true },
  { key: 'expectedEffort', label: 'Total Expected Effort (p.h.)', numeric: true },
  { key: 'totalLogged', label: 'Total Logged Effort (p.h.)', numeric: true },
  { key: 'unloggedEffort', label: 'Total Unlogged Effort (p.h.)', numeric: true },
  { key: 'weekendHoliday', label: 'Weekend/ Holiday (p.h.)', numeric: true },
];

const buildProjectServiceApiUrl = (path) => {
  if (!path) return '';

  const servicePath = appendServiceNameToURL(PROJECT_SERVICE_NAME);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${servicePath}/api${path}`;
  }

  const servicePort = appendServicePortToURL(PROJECT_SERVICE_NAME);
  return `${process.env.REACT_APP_API_URL}:${servicePort}${servicePath}/api${path}`;
};
const REPORT_BASE_URL = buildProjectServiceApiUrl(FILTER_URL_BASE);
const PROJECT_NAME_FILTER_URL = buildProjectServiceApiUrl(`${FILTER_URL_BASE}/project-names`);
const buildProjectDetailsUrl = (projectId) => (
  `${PROJECT_DETAILS_ROUTE}?projectId=${encodeURIComponent(String(projectId))}`
);

const FILTER_ONLY_COLUMNS = [
  {
    key: 'projectVertical',
    label: 'Project Vertical',
    filterTemplate: 'multiSelect',
    filterUrl: buildProjectServiceApiUrl(`${FILTER_URL_BASE}/vertical-names`),
  },
  {
    key: 'projectType',
    label: 'Project Type',
    filterTemplate: 'multiSelect',
    filterUrl: buildProjectServiceApiUrl(`${FILTER_URL_BASE}/project-type`),
  },
  {
    key: 'projectName',
    label: 'Project Name',
    filterTemplate: 'singleSelect',
    filterUrl: PROJECT_NAME_FILTER_URL,
  },
  {
    key: 'projectStatus',
    label: 'Project Status',
    filterTemplate: 'multiSelect',
    filterUrl: buildProjectServiceApiUrl(`${FILTER_URL_BASE}/project-status`),
  },
  {
    key: 'dateRange',
    label: 'Start Date / End Date',
    filterTemplate: 'dateRange',
    filterUrl: '',
  },
];
const PROJECT_NAME_DEPENDENCY_FIELDS = ['projectVertical', 'projectType', 'projectStatus'];

const getSelectedProjectName = (selectedProject) => {
  if (!selectedProject) {
    return '';
  }

  if (typeof selectedProject === 'string') {
    return selectedProject.trim();
  }

  return String(
    selectedProject?.name
    || selectedProject?.label
    || selectedProject?.value
    || selectedProject?.id
    || '',
  ).trim();
};

const getProjectNameFromFilters = (filters = []) => {
  const projectNameFilter = Array.isArray(filters)
    ? filters.find((filterItem) => filterItem?.field === 'projectName')
    : filters?.projectName || null;

  const selectedProject = projectNameFilter?.selectedValue
    || projectNameFilter?.selectedValues?.[0]
    || projectNameFilter;

  if (!selectedProject) {
    return '';
  }

  return getSelectedProjectName(selectedProject);
};

const parseRequestPayload = (response = {}) => {
  const requestData = response?.config?.data;

  if (!requestData) return {};

  if (typeof requestData === 'string') {
    try {
      return JSON.parse(requestData);
    } catch {
      return {};
    }
  }

  return typeof requestData === 'object' ? requestData : {};
};

const getProjectNameFromRequestPayload = (response = {}) => {
  const requestPayload = parseRequestPayload(response);
  const candidateContainers = [
    requestPayload?.filter,
    requestPayload?.filters,
    requestPayload?.filterCriteria,
    requestPayload?.additionalPayloadData,
    requestPayload,
  ].filter(Boolean);

  const projectNameCandidate = candidateContainers
    .map((container) => {
      if (Array.isArray(container)) {
        return container.find((item) => item?.field === 'projectName') || null;
      }

      return container?.projectName || null;
    })
    .find(Boolean);

  if (!projectNameCandidate || Array.isArray(projectNameCandidate)) {
    return '';
  }

  if (projectNameCandidate?.selectedValue) {
    return getSelectedProjectName(projectNameCandidate.selectedValue);
  }

  return getSelectedProjectName(projectNameCandidate);
};

const getResponsePayload = (response = {}) => {
  const responseData = response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return {};
  }

  const nestedPayload = responseData?.data;
  const hasNestedTablePayload = nestedPayload
    && typeof nestedPayload === 'object'
    && (
      Array.isArray(nestedPayload?.tableData)
      || Array.isArray(nestedPayload?.tableConfiguration)
    );

  return hasNestedTablePayload ? nestedPayload : responseData;
};

const getResponseMeta = (response = {}, payload = {}) => {
  const responseData = response?.data;

  return payload?.meta
    || response?.meta
    || responseData?.meta
    || responseData?.data?.meta
    || {};
};

const toPositiveInteger = (value, fallbackValue) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : fallbackValue;
};

const getReportTitleFromConfiguration = (
  response,
  tableConfiguration = [],
  defaultTitle = 'Employee Utilization Report',
) => {
  const selectedProjectFilter = getFilterItemByField(tableConfiguration, 'projectName');
  const requestProjectName = getProjectNameFromRequestPayload(response);
  const responseProjectName = String(
    response?.data?.project?.projectName
    || response?.data?.data?.project?.projectName
    || '',
  ).trim();
  const projectName = getSelectedProjectName(getSelectedProjectFilterValue(selectedProjectFilter))
    || requestProjectName
    || responseProjectName;

  return projectName ? `Employee Utilization Report - ${projectName}` : defaultTitle;
};

const getSelectedProjectFilterValue = (filterItem) => (
  filterItem?.selectedValue
  || filterItem?.selectedValues?.[0]
  || null
);

const buildEmptyProjectNameFilterPayload = () => ({
  selectedValue: null,
  selectedValues: [],
});

const clearProjectNameFromFilterPayload = (filterPayload = {}) => ({
  ...cloneFilterPayload(filterPayload),
  projectName: buildEmptyProjectNameFilterPayload(),
});

const buildExportTitle = (context = {}) => {
  return buildExportFileName(context);
};

const sanitizeExportSegment = (value = '') => String(value)
  .replace(/[\\/:*?"<>|]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const sanitizeSheetNameSegment = (value = '') => String(value)
  .replace(/[\\/:*?]/g, ' ')
  .replace(/\[/g, ' ')
  .replace(/]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeSheetName = (value = '') => sanitizeSheetNameSegment(value).slice(0, 31);

const getProjectNameFromExportContext = (context = {}) => {
  const payload = context?.generalSettings?.additionalPayloadData || {};
  const filterProjectName = getProjectNameFromFilters(context?.filters);
  const payloadProjectName = payload?.projectName?.selectedValue
    || payload?.projectName?.selectedValues?.[0]
    || payload?.projectName;

  return getSelectedProjectName(filterProjectName || payloadProjectName);
};

const getReportMonthLabelFromExport = (context = {}) => {
  const payload = context?.generalSettings?.additionalPayloadData || {};
  const filterDateRange = context?.filters?.dateRange?.range;
  const payloadDateRange = payload?.dateRange?.range;
  const [startDate, endDate] = Array.isArray(filterDateRange) && filterDateRange.length > 0
    ? filterDateRange
    : Array.isArray(payloadDateRange) && payloadDateRange.length > 0
      ? payloadDateRange
      : [payload?.startDate || payload?.fromdate || null, payload?.endDate || payload?.todate || null];
  const reportDate = dayjs(startDate || endDate);

  return reportDate.isValid() ? reportDate.format('MMM-YYYY') : dayjs().format('MMM-YYYY');
};

const buildExportFileName = (context = {}) => {
  const projectName = sanitizeExportSegment(getProjectNameFromExportContext(context));
  const monthLabel = sanitizeExportSegment(getReportMonthLabelFromExport(context));

  return projectName
    ? `Emp-utilization-report-${projectName}-${monthLabel}`
    : `Emp-utilization-report-${monthLabel}`;
};

const buildExportSheetName = () => normalizeSheetName('Utilization Activity Report');

const getReportHeading = (appliedFilters = {}) => {
  const projectName = getProjectNameFromFilters(appliedFilters);

  return projectName
    ? `Employee Utilization Report - ${projectName}`
    : 'Employee Utilization Report';
};

const STATIC_COLUMN_KEYS = STATIC_COLUMNS.map((column) => column.key);
const getColumnField = (column) => column?.field || column?.key || '';

const getColumnLabel = (column) => column?.header || column?.label || getColumnField(column);

const getColumnNumeric = (column) => Boolean(column?.numeric);

const getColumnClassName = (field) => (
  field ? `employee-utilization-col--${field}` : ''
);

const DAY_HEADER_PATTERN = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(.+)$/;
const HOLIDAY_DAY_HEADER_PATTERN = /^Holiday\s+(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(.+)$/;
const HOLIDAY_LIST_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ACTIVITY_REPORT_DATE_PATTERN = /^\d{2}-[A-Za-z]{3}-\d{4}$/;
const HEADER_WIDTH_FALLBACK_CHAR_PX = 7.4;
const HEADER_WIDTH_PADDING_PX = 30;
const HEADER_WIDTH_MIN_PX = 90;
const HEADER_WIDTH_MAX_PX = 220;
const dayHeaderLabelCache = new Map();
const headerWidthCache = new Map();
const SORT_OPTIONS = [
  { label: 'Sort Ascending', icon: 'pi pi-arrow-up', order: 1 },
  { label: 'Sort Descending', icon: 'pi pi-arrow-down', order: -1 },
];
const parseDayHeaderLabel = (label = '') => {
  const normalizedLabel = String(label).trim();

  if (!normalizedLabel) {
    return null;
  }

  if (dayHeaderLabelCache.has(normalizedLabel)) {
    return dayHeaderLabelCache.get(normalizedLabel);
  }

  const holidayHeaderMatch = normalizedLabel.match(HOLIDAY_DAY_HEADER_PATTERN);

  if (holidayHeaderMatch) {
    const [, rawDayName, dateLabel] = holidayHeaderMatch;

    const parsedLabel = {
      rawDayName,
      displayDayName: rawDayName,
      dateLabel,
      isHoliday: true,
    };

    dayHeaderLabelCache.set(normalizedLabel, parsedLabel);
    return parsedLabel;
  }

  const dayHeaderMatch = normalizedLabel.match(DAY_HEADER_PATTERN);

  if (!dayHeaderMatch) {
    dayHeaderLabelCache.set(normalizedLabel, null);
    return null;
  }

  const [, rawDayName, dateLabel] = dayHeaderMatch;

  const parsedLabel = {
    rawDayName,
    displayDayName: rawDayName,
    dateLabel,
    isHoliday: false,
  };

  dayHeaderLabelCache.set(normalizedLabel, parsedLabel);
  return parsedLabel;
};

const normalizeWeekendDayColumnLabel = (column = {}) => {
  const label = getColumnLabel(column);
  const parsedDayHeader = parseDayHeaderLabel(label);

  if (!parsedDayHeader) {
    return column;
  }

  const { displayDayName, dateLabel, isHoliday } = parsedDayHeader;
  const normalizedLabel = (Boolean(column?.isHoliday) || isHoliday)
    ? `Holiday ${displayDayName} ${dateLabel}`
    : `${displayDayName} ${dateLabel}`;

  if (normalizedLabel === label) {
    return column;
  }

  return {
    ...column,
    header: column?.header === undefined ? normalizedLabel : (
      typeof column.header === 'string' ? normalizedLabel : column.header
    ),
    label: column?.label === undefined ? normalizedLabel : (
      typeof column.label === 'string' ? normalizedLabel : column.label
    ),
  };
};

const normalizeHolidayDate = (value = '') => {
  const trimmedValue = String(value || '').trim();

  if (!trimmedValue) {
    return '';
  }

  if (HOLIDAY_LIST_DATE_PATTERN.test(trimmedValue)) {
    return trimmedValue;
  }

  const dateMatch = trimmedValue.match(ACTIVITY_REPORT_DATE_PATTERN);

  if (!dateMatch) {
    return '';
  }

  const [, day, monthShort, year] = trimmedValue.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/) || [];
  if (!day || !monthShort || !year) {
    return '';
  }

  const monthMap = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  };

  return monthMap[monthShort]
    ? `${year}-${monthMap[monthShort]}-${day}`
    : '';
};

const getHolidayDateFromColumn = (column = {}) => (
  normalizeHolidayDate(column?.dateKey)
    || normalizeHolidayDate(
      String(getColumnLabel(column))
        .split(' ')
        .pop(),
    )
);

const areDateRangesEqual = (leftRange = [], rightRange = []) => (
  JSON.stringify(leftRange) === JSON.stringify(rightRange)
);

const decorateHolidayColumns = (tableConfiguration = [], holidayDates = new Set()) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => {
      const holidayDate = getHolidayDateFromColumn(column);
      const isHoliday = Boolean(column?.isHoliday)
        || (holidayDate ? holidayDates.has(holidayDate) : false);

      return {
        ...column,
        isHoliday,
      };
    })
    : []
);

const isDayColumn = (column) => Boolean(parseDayHeaderLabel(getColumnLabel(column)));

const renderEmployeeDayHeader = (dayName, dateLabel, isHoliday = false) => (
  <span className="employee-utilization-day-header">
    {isHoliday ? (
      <span className="employee-utilization-day-header-holiday">Holiday</span>
    ) : null}
    <span className="employee-utilization-day-header-day">{dayName}</span>
    <span className="employee-utilization-day-header-date">{dateLabel}</span>
  </span>
);

const renderMetricHeader = (label) => {
  const normalizedLabel = String(label || '').trim();
  const effortMatch = normalizedLabel.match(/^(.*)\s+(Effort)\s+\((p\.h\.)\)$/i);

  if (effortMatch) {
    return (
      <span className="employee-utilization-metric-header">
        <span className="employee-utilization-metric-header-title">{effortMatch[1]}</span>
        <span className="employee-utilization-metric-header-unit">
          {effortMatch[2]} ({effortMatch[3]})
        </span>
      </span>
    );
  }

  const match = normalizedLabel.match(/^(.*)\s+\((p\.h\.)\)$/i);

  if (!match) {
    return normalizedLabel;
  }

  return (
    <span className="employee-utilization-metric-header">
      <span className="employee-utilization-metric-header-title">{match[1]}</span>
      <span className="employee-utilization-metric-header-unit">({match[2]})</span>
    </span>
  );
};

const getRenderedHeader = (column) => {
  const label = getColumnLabel(column);
  const field = getColumnField(column);
  const parsedDayHeader = parseDayHeaderLabel(label);

  if (!parsedDayHeader) {
    if ([
      'expectedEffort',
      'totalLogged',
      'unloggedEffort',
      'weekendHoliday',
    ].includes(field)) {
      return renderMetricHeader(label);
    }

    return label;
  }

  return renderEmployeeDayHeader(
    parsedDayHeader.displayDayName,
    parsedDayHeader.dateLabel,
    Boolean(column?.isHoliday) || parsedDayHeader.isHoliday,
  );
};

const getMeasuredHeaderWidth = (label = '') => {
  const normalizedLabel = String(label || '').replace(/\s+/g, ' ')
    .trim();

  if (!normalizedLabel) {
    return HEADER_WIDTH_MIN_PX;
  }

  if (headerWidthCache.has(normalizedLabel)) {
    return headerWidthCache.get(normalizedLabel);
  }

  if (typeof document === 'undefined') {
    const fallbackWidth = Math.ceil(normalizedLabel.length * HEADER_WIDTH_FALLBACK_CHAR_PX);
    headerWidthCache.set(normalizedLabel, fallbackWidth);
    return fallbackWidth;
  }

  const canvas = getMeasuredHeaderWidth.canvas
    || (getMeasuredHeaderWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');

  if (!context) {
    const fallbackWidth = Math.ceil(normalizedLabel.length * HEADER_WIDTH_FALLBACK_CHAR_PX);
    headerWidthCache.set(normalizedLabel, fallbackWidth);
    return fallbackWidth;
  }

  context.font = '600 13px Mukta, sans-serif';
  const measuredWidth = Math.ceil(context.measureText(normalizedLabel).width);
  headerWidthCache.set(normalizedLabel, measuredWidth);
  return measuredWidth;
};

const getColumnWidth = (column, index) => {
  const field = getColumnField(column);
  const label = getColumnLabel(column);

  if (isDayColumn(column)) return 150;
  if ([
    'workingDays',
    'leaveDays',
    'expectedEffort',
    'totalLogged',
    'unloggedEffort',
    'weekendHoliday',
  ].includes(field)) {
    return 150;
  }

  const measuredWidth = getMeasuredHeaderWidth(label) + HEADER_WIDTH_PADDING_PX;
  const baseWidth = Math.min(
    Math.max(measuredWidth, HEADER_WIDTH_MIN_PX),
    HEADER_WIDTH_MAX_PX,
  );

  if (field === 'employeeName') return Math.max(baseWidth, 140);
  if (field === 'employeeId') return Math.max(baseWidth, 110);

  return index === 0 ? Math.max(baseWidth, 140) : baseWidth;
};

const getForcedColumnWidth = (field, columnWidth, fallbackWidth) => (
  field === 'unloggedEffort'
    ? 200
    : [
      'employeeType',
      'workingDays',
      'leaveDays',
      'expectedEffort',
      'totalLogged',
      'weekendHoliday',
    ].includes(field)
      ? 180
      : (columnWidth ?? fallbackWidth)
);

const sortColumnsByOrder = (columns = []) => columns
  .map((column, index) => ({ column, index }))
  .sort((leftColumn, rightColumn) => {
    const leftOrder = Number(leftColumn.column?.order);
    const rightOrder = Number(rightColumn.column?.order);
    const normalizedLeftOrder = Number.isFinite(leftOrder) ? leftOrder : Number.MAX_SAFE_INTEGER;
    const normalizedRightOrder = Number.isFinite(rightOrder)
      ? rightOrder
      : Number.MAX_SAFE_INTEGER;

    if (normalizedLeftOrder !== normalizedRightOrder) {
      return normalizedLeftOrder - normalizedRightOrder;
    }

    return leftColumn.index - rightColumn.index;
  })
  .map(({ column }) => column);

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '' || value === '-') return null;
  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const indianNumberFormatter = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const MONTH_ABBREVIATION_TO_NUMBER = {
  jan: '01',
  feb: '02',
  mar: '03',
  apr: '04',
  may: '05',
  jun: '06',
  jul: '07',
  aug: '08',
  sep: '09',
  oct: '10',
  nov: '11',
  dec: '12',
};

const formatMetricValue = (value) => {
  if (!Number.isFinite(value)) return '0.00';
  const roundedValue = Math.round(value * 100) / 100;
  return indianNumberFormatter.format(roundedValue);
};

const getEmployeeTypeFromStatusFlags = (row = {}) => {
  if (row?.employeeType) return row.employeeType;
  if (row?.isMovedOut) return 'Moved Out';
  if (row?.isUnallocated) return 'Un allocated';
  return row?.employeeType;
};

const normalizeNumericDisplayRows = (columns = [], rows = []) => {
  const numericFields = (Array.isArray(columns) ? columns : [])
    .filter((column) => getColumnNumeric(column))
    .map((column) => getColumnField(column))
    .filter(Boolean);

  return (Array.isArray(rows) ? rows : []).map((row) => {
    const normalizedRow = { ...row };
    normalizedRow.employeeType = getEmployeeTypeFromStatusFlags(row);

    for (let index = 0; index < numericFields.length; index += 1) {
      const field = numericFields[index];
      normalizedRow[field] = formatMetricValue(parseNumericValue(row?.[field]) ?? 0);
    }

    return normalizedRow;
  });
};

const clearDailyEffortCellClasses = (rootNode) => {
  rootNode
    ?.querySelectorAll?.(`.${DAILY_CELL_STATE_CLASSES.join(',.')}`)
    ?.forEach((cellElement) => {
      cellElement.classList.remove(...DAILY_CELL_STATE_CLASSES);
      cellElement.style.removeProperty('background-color');
    });
};

const normalizeHeaderText = (value = '') => String(value)
  .replace(/\s+/g, '')
  .toLowerCase()
  .trim();

const getColumnHeaderLookup = (columns = []) => new Map(
  sortColumnsByOrder(Array.isArray(columns) ? columns : [])
    .filter((column) => column?.visible !== false)
    .map((column) => [
      normalizeHeaderText(getColumnLabel(column)),
      getColumnField(column),
    ]),
);

const getRenderedBodyFields = (bodyElement, columns = []) => {
  const dataTable = bodyElement.closest('.p-datatable-table');
  const headerCells = dataTable?.querySelectorAll?.('.p-datatable-thead > tr > th') || [];
  const headerLookup = getColumnHeaderLookup(columns);
  const renderedFields = Array.from(headerCells)
    .map((headerCell) => headerLookup.get(normalizeHeaderText(headerCell.textContent)))
    .filter(Boolean);

  if (renderedFields.length) {
    return renderedFields;
  }

  return sortColumnsByOrder(Array.isArray(columns) ? columns : [])
    .filter((column) => column?.visible !== false)
    .map((column) => getColumnField(column))
    .filter(Boolean);
};

const getVisibleColumnFields = (columns = []) => sortColumnsByOrder(
  Array.isArray(columns) ? columns : [],
)
  .filter((column) => column?.visible !== false)
  .map((column) => getColumnField(column))
  .filter(Boolean);

const applyDailyEffortCellStyles = (rootNode, rows = [], columns = []) => {
  if (!rootNode) {
    return;
  }

  if (rootNode.querySelector('.p-skeleton')) {
    return;
  }

  const paintTargets = [];
  const bodyElements = rootNode.querySelectorAll('.p-datatable-tbody');
  bodyElements.forEach((bodyElement) => {
    const renderedFields = getRenderedBodyFields(bodyElement, columns);
    const visibleFields = getVisibleColumnFields(columns);
    const fieldIndexes = new Map(renderedFields.map((field, index) => [field, index]));
    const rowElements = Array.from(bodyElement.querySelectorAll(':scope > tr'));

    rowElements.forEach((rowElement, rowIndex) => {
      const row = rows[rowIndex];
      if (!row || row.id === 'total-row') {
        return;
      }

      const cellElements = rowElement.querySelectorAll('td');
      const visibleFieldIndexes = new Map(visibleFields.map((field, index) => [field, index]));
      const frozenColumnOffset = Math.max(visibleFields.length - cellElements.length, 0);

      Object.entries(row.dailyCellStates || {}).forEach(([field, state]) => {
        const stateClass = DAILY_CELL_STATE_CLASS_MAP[state];
        const cellIndex = fieldIndexes.get(field)
          ?? (
            visibleFieldIndexes.has(field)
              ? visibleFieldIndexes.get(field) - frozenColumnOffset
              : undefined
          );
        if (!stateClass || cellIndex === undefined) {
          return;
        }

        const cellElement = cellElements[cellIndex];
        if (cellElement) {
          paintTargets.push({
            cellElement,
            stateClass,
          });
        }
      });
    });
  });

  if (!paintTargets.length) {
    return;
  }

  clearDailyEffortCellClasses(rootNode);
  paintTargets.forEach(({ cellElement, stateClass }) => {
    cellElement.classList.add(stateClass);
  });
};

const normalizeSingleSelectOption = (option) => {
  if (option === null || option === undefined) return null;

  if (typeof option === 'string') {
    const normalizedValue = option.trim();
    return normalizedValue
      ? {
        id: normalizedValue,
        name: normalizedValue,
        label: normalizedValue,
        value: normalizedValue,
      }
      : null;
  }

  if (typeof option !== 'object') return null;

  const normalizedValue = String(
    option?.value
    || option?.label
    || option?.name
    || option?.id
    || '',
  ).trim();

  if (!normalizedValue) return null;

  return {
    id: normalizedValue,
    name: String(option?.name || option?.label || normalizedValue).trim(),
    ...option,
    label: String(option?.label || option?.name || normalizedValue).trim(),
    value: normalizedValue,
  };
};

const mergeSingleSelectOptions = (...optionGroups) => {
  const normalizedOptions = optionGroups
    .flatMap((optionGroup) => (Array.isArray(optionGroup) ? optionGroup : []))
    .map(normalizeSingleSelectOption)
    .filter(Boolean);

  return [...new Map(
    normalizedOptions.map((option) => [option.id || option.value, option]),
  ).values()].sort((leftOption, rightOption) => (
    String(leftOption.label || leftOption.name).localeCompare(String(rightOption.label || rightOption.name))
  ));
};

const buildMenuItems = ({
  field,
  header,
  template = 'multiSelect',
  numeric = false,
  filterUrl = '',
  filterOptions = [],
  sortable = true,
  isGlobalFilter = false,
  initialRange = undefined,
}) => [{
  label: header,
  items: [
    ...(sortable
      ? [{
        type: 'sort',
        showSortButtonInHeader: true,
        serverSideSorting: true,
        field,
        options: SORT_OPTIONS,
        valueType: numeric ? 'number' : 'string',
      }, { separator: true }]
      : []),
    {
      type: 'filter',
      field,
      template,
      tableUtilityConfig: {
        ...(field === 'projectName'
          ? {
            requiredOnMainApply: true,
            requiredOnMainApplyWhenFieldsActive: ['projectVertical', 'projectType', 'projectStatus'],
            requiredValidationMessage: 'Select Project Name before applying filters.',
          }
          : {}),
        ...(template === 'dateRange'
          ? {
            maxRangeDays: 32,
            maxRangeValidationMessage: DATE_RANGE_LIMIT_ERROR,
          }
          : {}),
      },
      filterSection: template === 'dateRange' ? 'condition' : 'value',
      ...(template === 'singleSelect'
        ? { selectedValue: null }
        : { selectedValues: [] }),
      inputValue: template === 'inputfield' ? '' : undefined,
      range:
        template === 'range' || template === 'dateRange'
          ? (Array.isArray(initialRange) ? initialRange : [null, null])
          : undefined,
      url: filterUrl || undefined,
      values: Array.isArray(filterOptions) && filterOptions.length ? filterOptions : undefined,
      options: Array.isArray(filterOptions) && filterOptions.length ? filterOptions : undefined,
      label:
        template === 'dateRange'
          ? 'Filter by date range'
          : `Filter By ${header}`,
      outsideFilterSections: template === 'dateRange' ? ['condition'] : undefined,
      conditionOptions: template === 'dateRange'
        ? [
          { label: 'Is Between', value: 'between', inputMode: 'range' },
        ]
        : undefined,
      isGlobalFilter,
      condition:
        template === 'dateRange'
          ? 'between'
          : template === 'numberFilter'
            ? null
            : undefined,
      value: template === 'numberFilter' ? null : undefined,
      dependentFieldsToReset: ['projectVertical', 'projectType', 'projectStatus'].includes(field)
        ? ['projectName']
        : undefined,
      isRequired: field === 'projectName',
      requiredOnMainApply: field === 'projectName',
      requiredOnMainApplyWhenFieldsActive:
        field === 'projectName'
          ? ['projectVertical', 'projectType', 'projectStatus', 'dateRange']
          : undefined,
      requiredMessage:
        field === 'projectName'
          ? 'Select Project Name before applying filters.'
          : undefined,
    },
  ],
}];

const getInitialDateRangeFilter = (appliedFilters = {}) => {
  const startDate = String(appliedFilters?.startDate || '').trim();
  const endDate = String(appliedFilters?.endDate || '').trim();

  if (!startDate || !endDate) {
    return undefined;
  }

  return [startDate, endDate];
};

const getFilterItemByField = (tableConfiguration = [], field) => {
  if (!Array.isArray(tableConfiguration) || !field) {
    return null;
  }

  for (const column of tableConfiguration) {
    const menuItems = Array.isArray(column?.menuItems) ? column.menuItems : [];

    for (const menuGroup of menuItems) {
      const items = Array.isArray(menuGroup?.items) ? menuGroup.items : [];
      const matchedItem = items.find(
        (item) => item?.type === 'filter' && item?.field === field,
      );

      if (matchedItem) {
        return matchedItem;
      }
    }
  }

  return null;
};

const buildCurrentFilterPayload = (tableConfiguration = []) => {
  const projectVerticalFilter = getFilterItemByField(tableConfiguration, 'projectVertical');
  const projectTypeFilter = getFilterItemByField(tableConfiguration, 'projectType');
  const projectStatusFilter = getFilterItemByField(tableConfiguration, 'projectStatus');
  const projectStatusColorFilter = getFilterItemByField(tableConfiguration, 'projectStatusColor');
  const projectNameFilter = getFilterItemByField(tableConfiguration, 'projectName');
  const dateRangeFilter = getFilterItemByField(tableConfiguration, 'dateRange');
  const [rangeStartDate, rangeEndDate] = Array.isArray(dateRangeFilter?.range)
    ? dateRangeFilter.range
    : [undefined, undefined];
  const projectVerticalSelectedValues = Array.isArray(projectVerticalFilter?.selectedValues)
    ? projectVerticalFilter.selectedValues
    : [];
  const projectTypeSelectedValues = Array.isArray(projectTypeFilter?.selectedValues)
    ? projectTypeFilter.selectedValues
    : [];
  const projectStatusSelectedValues = Array.isArray(projectStatusFilter?.selectedValues)
    ? projectStatusFilter.selectedValues
    : [];
  const projectStatusColorSelectedValues = Array.isArray(projectStatusColorFilter?.selectedValues)
    ? projectStatusColorFilter.selectedValues
    : [];
  const projectNameSelectedValue = getSelectedProjectFilterValue(projectNameFilter);
  const dateRange = [rangeStartDate || null, rangeEndDate || null];

  return {
    projectVertical: {
      selectedValues: projectVerticalSelectedValues,
    },
    projectType: {
      selectedValues: projectTypeSelectedValues,
    },
    projectStatus: {
      selectedValues: projectStatusSelectedValues,
    },
    projectStatusColor: {
      selectedValues: projectStatusColorSelectedValues,
    },
    projectName: {
      selectedValue: projectNameSelectedValue,
      selectedValues: projectNameSelectedValue ? [projectNameSelectedValue] : [],
    },
    dateRange: {
      selectedValues: [],
      range: dateRange,
    },
  };
};

const buildDefaultAppliedFilterPayload = (initialDateRange = {}) => {
  const dateRange = [
    initialDateRange?.startDate || null,
    initialDateRange?.endDate || null,
  ];

  return {
    projectVertical: {
      selectedValues: [],
    },
    projectType: {
      selectedValues: [],
    },
    projectStatus: {
      selectedValues: [],
    },
    projectStatusColor: {
      selectedValues: [],
    },
    projectName: {
      selectedValue: null,
      selectedValues: [],
    },
    dateRange: {
      selectedValues: [],
      range: dateRange,
    },
  };
};

const buildAdditionalPayloadData = (filterPayload = {}) => {
  if (!filterPayload || typeof filterPayload !== 'object') {
    return {};
  }

  const [startDate, endDate] = getNormalizedDateRange(filterPayload);
  const projectVerticalSelectedValues = Array.isArray(filterPayload?.projectVertical?.selectedValues)
    ? filterPayload.projectVertical.selectedValues
    : [];
  const projectTypeSelectedValues = Array.isArray(filterPayload?.projectType?.selectedValues)
    ? filterPayload.projectType.selectedValues
    : [];
  const projectStatusSelectedValues = Array.isArray(filterPayload?.projectStatus?.selectedValues)
    ? filterPayload.projectStatus.selectedValues
    : [];
  const projectStatusColorSelectedValues = Array.isArray(filterPayload?.projectStatusColor?.selectedValues)
    ? filterPayload.projectStatusColor.selectedValues
    : [];
  const projectNameSelectedValue = getSelectedProjectFilterValue(filterPayload?.projectName);
  const payloadWithoutFilterFields = { ...filterPayload };

  delete payloadWithoutFilterFields.projectName;
  delete payloadWithoutFilterFields.dateRange;
  delete payloadWithoutFilterFields.startDate;
  delete payloadWithoutFilterFields.endDate;
  delete payloadWithoutFilterFields.fromdate;
  delete payloadWithoutFilterFields.todate;
  delete payloadWithoutFilterFields.filter;

  return {
    ...payloadWithoutFilterFields,
    projectVertical: projectVerticalSelectedValues,
    projectType: projectTypeSelectedValues,
    projectStatus: projectStatusSelectedValues,
    projectStatusColor: projectStatusColorSelectedValues,
    projectName: projectNameSelectedValue
      ? {
        selectedValue: projectNameSelectedValue,
        selectedValues: [projectNameSelectedValue],
      }
      : undefined,
    startDate,
    endDate,
    fromdate: startDate,
    todate: endDate,
  };
};

const cloneFilterPayload = (filterPayload = {}) => ({
  projectVertical: {
    selectedValues: Array.isArray(filterPayload?.projectVertical?.selectedValues)
      ? [...filterPayload.projectVertical.selectedValues]
      : [],
  },
  projectType: {
    selectedValues: Array.isArray(filterPayload?.projectType?.selectedValues)
      ? [...filterPayload.projectType.selectedValues]
      : [],
  },
  projectStatus: {
    selectedValues: Array.isArray(filterPayload?.projectStatus?.selectedValues)
      ? [...filterPayload.projectStatus.selectedValues]
      : [],
  },
  projectStatusColor: {
    selectedValues: Array.isArray(filterPayload?.projectStatusColor?.selectedValues)
      ? [...filterPayload.projectStatusColor.selectedValues]
      : [],
  },
  projectName: {
    selectedValue: getSelectedProjectFilterValue(filterPayload?.projectName) || null,
    selectedValues: Array.isArray(filterPayload?.projectName?.selectedValues)
      ? [...filterPayload.projectName.selectedValues]
      : [],
  },
  dateRange: {
    selectedValues: Array.isArray(filterPayload?.dateRange?.selectedValues)
      ? [...filterPayload.dateRange.selectedValues]
      : [],
    range: Array.isArray(filterPayload?.dateRange?.range)
      ? [...filterPayload.dateRange.range]
      : [null, null],
  },
});

const buildFilterPayloadFromRequest = (requestPayload = {}) => {
  const requestFilters = Array.isArray(requestPayload?.filter) ? requestPayload.filter : [];
  const getRequestFilterByField = (field) => requestFilters.find((item) => item?.field === field) || null;
  const getRequestSelectedValues = (field) => {
    const filterItem = getRequestFilterByField(field);
    if (Array.isArray(filterItem?.selectedValues)) {
      return filterItem.selectedValues.filter((value) => value !== null && value !== undefined);
    }

    const topLevelValue = requestPayload?.[field];
    return Array.isArray(topLevelValue)
      ? topLevelValue.filter((value) => value !== null && value !== undefined)
      : [];
  };
  const requestProjectNameFilter = getRequestFilterByField('projectName');
  const topLevelProjectNameValue = getSelectedProjectFilterValue(requestPayload?.projectName);
  const filterProjectNameValue = getSelectedProjectFilterValue(requestProjectNameFilter);
  const selectedProjectNameValue = topLevelProjectNameValue || filterProjectNameValue || null;
  const dateRangeFilter = getRequestFilterByField('dateRange');
  const requestDateRange = Array.isArray(dateRangeFilter?.range)
    ? dateRangeFilter.range
    : [requestPayload?.startDate || requestPayload?.fromdate || null, requestPayload?.endDate || requestPayload?.todate || null];

  return cloneFilterPayload({
    projectVertical: {
      selectedValues: getRequestSelectedValues('projectVertical'),
    },
    projectType: {
      selectedValues: getRequestSelectedValues('projectType'),
    },
    projectStatus: {
      selectedValues: getRequestSelectedValues('projectStatus'),
    },
    projectStatusColor: {
      selectedValues: getRequestSelectedValues('projectStatusColor'),
    },
    projectName: {
      selectedValue: selectedProjectNameValue,
      selectedValues: selectedProjectNameValue ? [selectedProjectNameValue] : [],
    },
    dateRange: {
      selectedValues: [],
      range: Array.isArray(requestDateRange) ? requestDateRange : [null, null],
    },
  });
};

const mergeFilterPayloads = (primaryPayload = {}, fallbackPayload = {}) => {
  const nextPayload = cloneFilterPayload(primaryPayload);
  const fallback = cloneFilterPayload(fallbackPayload);

  ['projectVertical', 'projectType', 'projectStatus', 'projectStatusColor'].forEach((field) => {
    const primarySelectedValues = Array.isArray(nextPayload?.[field]?.selectedValues)
      ? nextPayload[field].selectedValues.filter(Boolean)
      : [];
    const fallbackSelectedValues = Array.isArray(fallback?.[field]?.selectedValues)
      ? fallback[field].selectedValues.filter(Boolean)
      : [];

    if (!primarySelectedValues.length && fallbackSelectedValues.length) {
      nextPayload[field].selectedValues = fallbackSelectedValues;
    }
  });

  if (!getSelectedProjectFilterValue(nextPayload?.projectName)) {
    const fallbackProjectValue = getSelectedProjectFilterValue(fallback?.projectName);
    nextPayload.projectName = {
      selectedValue: fallbackProjectValue || null,
      selectedValues: fallbackProjectValue ? [fallbackProjectValue] : [],
    };
  }

  if (!hasCompleteDateRange(nextPayload?.dateRange?.range) && hasCompleteDateRange(fallback?.dateRange?.range)) {
    nextPayload.dateRange = {
      ...nextPayload.dateRange,
      range: [...fallback.dateRange.range],
    };
  }

  return nextPayload;
};

const normalizeDateInputValue = (value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return null;

  const isoMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return normalizedValue;

  const displayMatch = normalizedValue.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (displayMatch) {
    const [, day, month, year] = displayMatch;
    const monthNumber = MONTH_ABBREVIATION_TO_NUMBER[month.toLowerCase()];
    return monthNumber ? `${year}-${monthNumber}-${day.padStart(2, '0')}` : null;
  }

  return null;
};

const DATE_RANGE_INPUT_SELECTORS = [
  '.tu-value-condition-filter input',
  '.calendar-filter-container input',
  '.tu-filter-panel-template input',
  '.tu-filter-detail-template input',
  '.tu-filter-template-shell input',
  '.tu-outside-filter-section-body input',
];

const getDateRangeInputs = (rootElement) => {
  if (!rootElement?.querySelectorAll) {
    return [];
  }

  const inputElements = new Set();
  DATE_RANGE_INPUT_SELECTORS.forEach((selector) => {
    rootElement.querySelectorAll(selector).forEach((inputElement) => {
      inputElements.add(inputElement);
    });
  });

  return [...inputElements];
};

const DATE_RANGE_FILTER_ROOT_SELECTOR = [
  '.tu-outside-filter-section',
  '.tu-value-condition-filter',
  '.tu-filter-template-panel',
  '.tu-filter-template-shell',
].join(', ');

const getDateRangeFilterRoot = (rootElement) => {
  if (!rootElement) {
    return null;
  }

  if (rootElement.matches?.(DATE_RANGE_FILTER_ROOT_SELECTOR)) {
    return rootElement;
  }

  return rootElement.closest?.(DATE_RANGE_FILTER_ROOT_SELECTOR)
    || rootElement.querySelector?.(DATE_RANGE_FILTER_ROOT_SELECTOR)
    || null;
};

const getDateRangeValuesFromRoot = (rootElement) => {
  const resolvedRootElement = getDateRangeFilterRoot(rootElement) || rootElement;
  const visibleDateValues = getDateRangeInputs(resolvedRootElement)
    .filter((inputElement) => inputElement?.offsetParent !== null)
    .map((inputElement) => normalizeDateInputValue(inputElement?.value))
    .filter(Boolean);

  if (visibleDateValues.length < 2) {
    return [null, null];
  }

  return visibleDateValues.slice(0, 2);
};

const getPayloadWithDateRangeFields = (payload = {}) => {
  const [startDate, endDate] = Array.isArray(payload?.dateRange?.range)
    ? payload.dateRange.range
    : [payload?.startDate || payload?.fromdate || null, payload?.endDate || payload?.todate || null];

  return {
    ...payload,
    startDate: startDate || null,
    endDate: endDate || null,
    fromdate: startDate || null,
    todate: endDate || null,
  };
};

const getNormalizedDateRange = (payload = {}) => {
  const [startDate, endDate] = Array.isArray(payload?.dateRange?.range)
    ? payload.dateRange.range
    : [payload?.startDate || payload?.fromdate || null, payload?.endDate || payload?.todate || null];

  const normalizedStartDate = String(startDate || '').trim();
  const normalizedEndDate = String(endDate || '').trim();

  if (!normalizedStartDate || !normalizedEndDate) {
    return [null, null];
  }

  return [normalizedStartDate, normalizedEndDate];
};

const hasCompleteDateRange = (range = []) => {
  const [startDate, endDate] = Array.isArray(range) ? range : [];

  return Boolean(
    String(startDate || '').trim()
    && String(endDate || '').trim(),
  );
};

const DATE_RANGE_LIMIT_ERROR = 'Date range has to be within 31 days.';

const getDateRangeLimitError = (range = []) => {
  const [startDate, endDate] = Array.isArray(range) ? range : [];
  const normalizedStartDate = normalizeDateInputValue(startDate);
  const normalizedEndDate = normalizeDateInputValue(endDate);

  if (!normalizedStartDate || !normalizedEndDate) {
    return '';
  }

  return dayjs(normalizedEndDate).diff(dayjs(normalizedStartDate), 'day') > 31
    ? DATE_RANGE_LIMIT_ERROR
    : '';
};

const getVisibleDateRangeFromDom = (rootElement = document) => {
  if (typeof document === 'undefined') {
    return [null, null];
  }

  return getDateRangeValuesFromRoot(rootElement);
};

const syncVisibleDateRangeValidation = (rootElement = document) => {
  if (typeof document === 'undefined') {
    return;
  }

  const filterRoot = getDateRangeFilterRoot(rootElement);
  if (!filterRoot) {
    return;
  }

  const footerElement = filterRoot.querySelector(
    '.tu-value-condition-footer, .tu-outside-filter-section-footer',
  );
  const applyButton = filterRoot.querySelector(
    '.tu-value-condition-apply, .tu-outside-filter-apply',
  );

  if (!footerElement || !applyButton) {
    return;
  }

  const dateRangeError = getDateRangeLimitError(getDateRangeValuesFromRoot(filterRoot));
  let errorElement = filterRoot.querySelector('.employee-utilization-date-range-error');

  if (dateRangeError) {
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'employee-utilization-date-range-error';
      footerElement.insertAdjacentElement('beforebegin', errorElement);
    }
    errorElement.textContent = dateRangeError;
    applyButton.disabled = true;
    applyButton.classList.add('employee-utilization-date-range-apply-disabled');
    return;
  }

  if (errorElement) {
    errorElement.remove();
  }
  applyButton.disabled = false;
  applyButton.classList.remove('employee-utilization-date-range-apply-disabled');
};

const getFieldFromVisibleMultiSelectTitle = (title = '') => {
  const multiSelectFieldLabels = {
    projectVertical: ['project vertical'],
    projectType: ['project type'],
    projectStatus: ['project status'],
    projectStatusColor: ['project status color', 'filter by color', 'status color'],
  };
  const normalizedTitle = String(title || '')
    .trim()
    .toLowerCase();

  return Object.entries(multiSelectFieldLabels).find(([, aliases]) => (
    aliases.some((alias) => normalizedTitle.includes(alias))
  ))?.[0] || '';
};

const getVisibleMultiSelectLabels = (filterElement) => {
  if (!filterElement) {
    return [];
  }

  const selectedOptionLabels = [...filterElement.querySelectorAll('.tu-multi-select-option')]
    .filter((optionElement) => (
      optionElement.classList.contains('selected')
      || Boolean(optionElement.querySelector('.p-checkbox-box.p-highlight'))
      || optionElement.getAttribute('aria-selected') === 'true'
    ))
    .map((optionElement) => (
      optionElement.querySelector('.tu-multi-select-option-label')?.textContent
      || optionElement.textContent
    ))
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  if (selectedOptionLabels.length) {
    return selectedOptionLabels;
  }

  return [...filterElement.querySelectorAll('.custom-chip, .p-chip')]
    .filter((chipElement) => !chipElement.classList.contains('tu-multi-select-count-chip'))
    .map((chipElement) => String(chipElement.textContent || '').trim())
    .filter(Boolean);
};

const applyDateRangeToTableConfiguration = (tableConfiguration = [], range = []) => {
  if (!Array.isArray(tableConfiguration) || !hasCompleteDateRange(range)) {
    return tableConfiguration;
  }

  const [startDate, endDate] = range;

  tableConfiguration.forEach((column) => {
    if (!Array.isArray(column?.menuItems)) {
      return;
    }

    column.menuItems.forEach((menuGroup) => {
      if (!Array.isArray(menuGroup?.items)) {
        return;
      }

      menuGroup.items.forEach((item) => {
        if (item?.type === 'filter' && item?.field === 'dateRange') {
          item.range = [startDate, endDate];
          item.selectedValues = [];
        }
      });
    });
  });

  return tableConfiguration;
};

const mergeFilterPayloadIntoTableConfig = (tableConfiguration = [], filterPayload = {}) => {
  if (!Array.isArray(tableConfiguration) || !filterPayload || typeof filterPayload !== 'object') {
    return tableConfiguration;
  }

  const projectVerticalFilter = getFilterItemByField(tableConfiguration, 'projectVertical');
  const projectTypeFilter = getFilterItemByField(tableConfiguration, 'projectType');
  const projectStatusFilter = getFilterItemByField(tableConfiguration, 'projectStatus');
  const projectStatusColorFilter = getFilterItemByField(tableConfiguration, 'projectStatusColor');
  const projectNameFilter = getFilterItemByField(tableConfiguration, 'projectName');

  if (projectVerticalFilter) {
    projectVerticalFilter.selectedValues = Array.isArray(filterPayload?.projectVertical?.selectedValues)
      ? [...filterPayload.projectVertical.selectedValues]
      : [];
  }

  if (projectTypeFilter) {
    projectTypeFilter.selectedValues = Array.isArray(filterPayload?.projectType?.selectedValues)
      ? [...filterPayload.projectType.selectedValues]
      : [];
  }

  if (projectStatusFilter) {
    projectStatusFilter.selectedValues = Array.isArray(filterPayload?.projectStatus?.selectedValues)
      ? [...filterPayload.projectStatus.selectedValues]
      : [];
  }

  if (projectStatusColorFilter) {
    projectStatusColorFilter.selectedValues = Array.isArray(filterPayload?.projectStatusColor?.selectedValues)
      ? [...filterPayload.projectStatusColor.selectedValues]
      : [];
  }

  if (projectNameFilter) {
    const selectedProjectValue = getSelectedProjectFilterValue(filterPayload?.projectName);
    projectNameFilter.selectedValue = selectedProjectValue || null;
    projectNameFilter.selectedValues = selectedProjectValue ? [selectedProjectValue] : [];
  }

  applyDateRangeToTableConfiguration(
    tableConfiguration,
    getNormalizedDateRange(filterPayload),
  );

  return tableConfiguration;
};

const buildDayColumnLabelsFromRange = (range = []) => {
  if (!hasCompleteDateRange(range)) {
    return [];
  }

  const [startDate, endDate] = range;
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
    return [];
  }

  const labels = [];
  let current = start;

  while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
    labels.push(current.format('dddd DD-MMM-YYYY'));
    current = current.add(1, 'day');
  }

  return labels;
};

const syncDayColumnLabelsToRange = (columns = [], range = []) => {
  if (!Array.isArray(columns) || !hasCompleteDateRange(range)) {
    return columns;
  }

  const dayColumns = columns.filter((column) => isDayColumn(column));
  const nextLabels = buildDayColumnLabelsFromRange(range);

  if (!dayColumns.length || dayColumns.length !== nextLabels.length) {
    return columns;
  }

  let dayColumnIndex = 0;

  return columns.map((column) => {
    if (!isDayColumn(column)) {
      return column;
    }

    const nextLabel = nextLabels[dayColumnIndex];
    dayColumnIndex += 1;

    return normalizeWeekendDayColumnLabel({
      ...column,
      header: nextLabel,
      label: nextLabel,
    });
  });
};

const applyInlineStyles = (element, styles = {}) => {
  if (!element) return;
  Object.assign(element.style, styles);
};

const applyEmployeeUtilizationHeaderClass = (rootNode) => {
  if (!rootNode) return;

  const headers = rootNode.querySelectorAll('.project-overview-header');
  headers.forEach((headerElement) => {
    headerElement.classList.add('employee-utilization-header');
  });
};

const applyEmployeeDayHeaders = (rootNode, columns = []) => {
  if (!rootNode) return;

  const dayHeaderMap = new Map(
    (Array.isArray(columns) ? columns : [])
      .filter((column) => isDayColumn(column))
      .map((column) => [getColumnField(column), column]),
  );

  dayHeaderMap.forEach((column, field) => {
    const label = getColumnLabel(column);
    const safeFieldSelector = String(field).replace(/([:.#[\],])/g, '\\$1');
    const headerCells = rootNode.querySelectorAll(
      `.employee-utilization-col--${safeFieldSelector}`,
    );
    const parsedDayHeader = parseDayHeaderLabel(label);
    const isHoliday = Boolean(column?.isHoliday) || Boolean(parsedDayHeader?.isHoliday);

    if (!parsedDayHeader) {
      return;
    }

    headerCells.forEach((headerCell) => {
      headerCell.setAttribute('data-day-name', parsedDayHeader.displayDayName);
      headerCell.setAttribute('data-day-date', parsedDayHeader.dateLabel);
      headerCell.setAttribute('data-day-holiday', isHoliday ? 'true' : 'false');
      headerCell.classList.add('employee-utilization-day-header-cell');
      headerCell.classList.toggle('employee-utilization-day-header-cell--holiday', isHoliday);

      const titleElement = headerCell.querySelector('.typography.header')
        || headerCell.querySelector('.p-column-title');

      if (titleElement) {
        titleElement.setAttribute('data-day-date', parsedDayHeader.dateLabel);
        titleElement.setAttribute('data-day-holiday', isHoliday ? 'true' : 'false');
      }
    });
  });
};

const applyEmployeeTableViewport = (rootNode) => {
  if (!rootNode) return;

  const tableContainers = rootNode.querySelectorAll('.table-container');
  tableContainers.forEach((tableContainerElement) => {
    tableContainerElement.classList.add('employee-utilization-table-viewport');
  });
};

const applyEmployeeUtilizationIconClass = () => {
  const tds = document.querySelectorAll('td[style*="border-left: none"]');

  tds.forEach((td) => {
    const icon = td.querySelector('.pi.pi-angle-right');
    if (icon) {
      icon.classList.add('employee-utilization-angle-right-icon');
    }
  });
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.employee-utilization-table-utility .button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton = !isFilterButton
      && !isResetButton
      && Boolean(buttonElement.querySelector('svg'));

    buttonElement.classList.toggle('employee-utilization-reset-btn', isResetButton);
    buttonElement.classList.toggle('employee-utilization-export-btn', isDownloadButton);
  });
};

const EmployeeUtilizationMoreActions = () => {
  const navigate = useNavigate();
  const moreMenuRef = useRef(null);

  const menuItems = useMemo(() => ([
    {
      label: 'Utilization Activity Report',
      command: () => navigate(UTILIZATION_ACTIVITY_REPORT_ROUTE),
    },
  ]), [navigate]);

  return (
    <Fragment>
      <Menu
        ref={moreMenuRef}
        model={menuItems}
        popup
        appendTo={document.body}
        className="employee-utilization-more-menu"
      />
      <button
        type="button"
        className="p-button p-component p-button-icon-only p-button-text employee-utilization-more-btn"
        onClick={(event) => moreMenuRef.current?.toggle(event)}
        aria-label="More utilization report links"
        title="More options"
      >
        <span className="employee-utilization-more-icon">
          <img src={moreIcon} alt="" />
        </span>
      </button>
    </Fragment>
  );
};

const applyNumericColumnAlignment = (rootNode, columns = []) => {
  if (!rootNode) return;

  const visibleColumns = (Array.isArray(columns) ? columns : [])
    .filter((column) => column?.visible !== false);

  visibleColumns.forEach((column, index) => {
    if (!getColumnNumeric(column)) {
      return;
    }

    const field = getColumnField(column);

    const columnPosition = index + 2;
    const headerCells = rootNode.querySelectorAll(
      `.p-datatable-thead > tr > th:nth-child(${columnPosition})`,
    );
    const bodyCells = rootNode.querySelectorAll(
      `.p-datatable-tbody > tr > td:nth-child(${columnPosition})`,
    );

    headerCells.forEach((cellElement) => {
      applyInlineStyles(cellElement, {
        textAlign: 'right',
      });

      const headerContent = cellElement.querySelector('.p-column-header-content');
      const headerColumn = cellElement.querySelector('.header-column');
      const titleElement = cellElement.querySelector('.p-column-title, .typography.header');

      [headerContent, headerColumn].filter(Boolean).forEach((element) => {
        applyInlineStyles(element, {
          display: 'flex',
          width: '100%',
          justifyContent: 'flex-end',
          textAlign: 'right',
        });
        if (['expectedEffort', 'totalLogged', 'unloggedEffort'].includes(field)) {
          applyInlineStyles(element, {
            paddingLeft: '0',
            paddingRight: '0',
          });
        }
      });

      if (titleElement) {
        applyInlineStyles(titleElement, {
          display: 'block',
          width: '100%',
          textAlign: 'right',
        });
        if (['expectedEffort', 'totalLogged', 'unloggedEffort'].includes(field)) {
          applyInlineStyles(titleElement, {
            paddingLeft: '0',
            paddingRight: '0',
          });
        }
      }
    });

    bodyCells.forEach((cellElement) => {
      applyInlineStyles(cellElement, {
        textAlign: 'right',
        paddingRight: '14px',
      });

      const wrapperElement = cellElement.querySelector('.cell-wrapper');
      const valueElement = cellElement.querySelector('.cell-wrapper .ellipses, .cell-wrapper > span, .cell-wrapper > div, span, div');

      if (wrapperElement) {
        applyInlineStyles(wrapperElement, {
          display: 'flex',
          width: '100%',
          justifyContent: 'flex-end',
          alignItems: 'center',
          textAlign: 'right',
        });
      }

      if (valueElement) {
        applyInlineStyles(valueElement, {
          display: 'block',
          width: '100%',
          textAlign: 'right',
        });
      }
    });
  });
};

const applyForcedColumnWidths = (rootNode, columns = []) => {
  if (!rootNode) return;

  const visibleColumns = (Array.isArray(columns) ? columns : [])
    .filter((column) => column?.visible !== false);

  visibleColumns.forEach((column, index) => {
    const field = getColumnField(column);
    if (field === 'employeeName') {
      return;
    }

    const forcedWidth = field === 'unloggedEffort'
      ? '200px'
      : '180px';

    const columnPosition = index + 2;
    const headerCells = rootNode.querySelectorAll(
      `.p-datatable-thead > tr > th:nth-child(${columnPosition})`,
    );
    const bodyCells = rootNode.querySelectorAll(
      `.p-datatable-tbody > tr > td:nth-child(${columnPosition})`,
    );

    [...headerCells, ...bodyCells].forEach((cellElement) => {
      applyInlineStyles(cellElement, {
        width: forcedWidth,
        minWidth: forcedWidth,
        maxWidth: forcedWidth,
        flex: `0 0 ${forcedWidth}`,
      });
    });
  });
};

const applyBodyCellHoverTitles = () => {
  const bodyCells = document.querySelectorAll(
    '.employee-utilization-table-utility .p-datatable-tbody > tr > td',
  );

  bodyCells.forEach((td) => {
    const contentElement
      = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;

    const fullText = (contentElement.textContent || '').trim();
    const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

    if (fullText && isTruncated) {
      td.setAttribute('title', fullText);
      contentElement.setAttribute('title', fullText);
    } else {
      td.removeAttribute('title');
      if (contentElement !== td) {
        contentElement.removeAttribute('title');
      }
    }
  });
};

const buildEmployeeFilterOptions = (rows = []) => mergeSingleSelectOptions(
  (Array.isArray(rows) ? rows : []).map((row) => {
    const employeeName = String(row?.employeeName || row?.employee_name || '').trim();
    return employeeName
      ? {
        id: employeeName,
        name: employeeName,
        label: employeeName,
        value: employeeName,
      }
      : null;
  }).filter(Boolean),
);

const buildResolvedColumns = (columns = [], filterOptionsByField = {}) => {
  const incomingColumns = Array.isArray(columns) ? columns : [];
  const incomingFields = incomingColumns.map(getColumnField).filter(Boolean);
  const containsStaticColumns = STATIC_COLUMN_KEYS.some(
    (field) => incomingFields.includes(field),
  );
  const filterOnlyColumns = FILTER_ONLY_COLUMNS
    .filter((column) => !incomingFields.includes(column.key))
    .map((column, index) => {
      const shouldUsePreloadedOptions = Array.isArray(filterOptionsByField[column.key])
        && filterOptionsByField[column.key].length;

      return {
        field: column.key,
        key: column.key,
        header: column.label,
        label: column.label,
        visible: false,
        filter: false,
        isGlobalFilter: true,
        sortable: column.key !== 'dateRange',
        order: STATIC_COLUMNS.length + incomingColumns.length + index + 1,
        filterTemplate: column.filterTemplate,
        filterUrl: shouldUsePreloadedOptions
          ? ''
          : column.filterUrl,
        menuItems: shouldUsePreloadedOptions
          ? buildMenuItems({
            field: column.key,
            header: column.label,
            template: column.filterTemplate,
            numeric: false,
            filterOptions: filterOptionsByField[column.key],
            sortable: column.key !== 'dateRange',
            isGlobalFilter: true,
          })
          : undefined,
      };
    });
  const orderedColumns = containsStaticColumns
    ? [...incomingColumns, ...filterOnlyColumns]
    : [...STATIC_COLUMNS, ...incomingColumns, ...filterOnlyColumns];

  return sortColumnsByOrder(orderedColumns);
};

const buildDisplayColumns = (
  resolvedColumns = [],
  filterOptionsByField = {},
) => resolvedColumns
  .map((column, index) => {
    const field = getColumnField(column);
    const isEmployeeNameColumn = field === 'employeeName';
    const resolvedDefaultPinned = typeof column?.defaultPinned === 'boolean'
      ? column.defaultPinned
      : isEmployeeNameColumn;
    const resolvedPinnable = isEmployeeNameColumn
      ? false
      : typeof column?.pinnable === 'boolean'
        ? column.pinnable
        : false;
    const resolvedPinVisible = isEmployeeNameColumn
      ? false
      : typeof column?.pinVisible === 'boolean'
        ? column.pinVisible
        : false;
    const resolvedShowPinButtonInHeader = typeof column?.showPinButtonInHeader === 'boolean'
      ? column.showPinButtonInHeader
      : resolvedPinVisible;
    const preloadedFilterOptions = Array.isArray(filterOptionsByField[field])
      ? filterOptionsByField[field]
      : [];
    const resolvedFilterOptions = mergeSingleSelectOptions(preloadedFilterOptions);
    const resolvedMenuItems = Array.isArray(column?.menuItems) && column.menuItems.length
      ? column.menuItems.map((menuGroup) => ({
        ...menuGroup,
        items: Array.isArray(menuGroup?.items)
          ? menuGroup.items.map((item) => {
            if (item?.type !== 'filter') {
              return item;
            }

            return {
              ...item,
              template:
                field === 'dateRange'
                  ? 'dateRange'
                  : item?.template,
              filterSection:
                field === 'dateRange' || item?.template === 'dateRange'
                  ? 'condition'
                  : item?.filterSection || 'value',
              values:
                field === 'dateRange' || field === 'projectName'
                  ? undefined
                  : mergeSingleSelectOptions(item?.values, item?.options, resolvedFilterOptions),
              options:
                field === 'dateRange' || field === 'projectName'
                  ? undefined
                  : mergeSingleSelectOptions(item?.options, item?.values, resolvedFilterOptions),
              range:
                field === 'dateRange'
                  ? (Array.isArray(item?.range) ? item.range : [null, null])
                  : item?.range,
              outsideFilterSections:
                field === 'dateRange'
                  ? ['condition']
                  : item?.outsideFilterSections,
              conditionOptions:
                field === 'dateRange'
                  ? [
                    { label: 'Is Between', value: 'between', inputMode: 'range' },
                  ]
                  : item?.conditionOptions,
              condition:
                field === 'dateRange'
                  ? 'between'
                  : item?.condition,
              url: item?.url || undefined,
              dependentFieldsToReset: ['projectVertical', 'projectType', 'projectStatus'].includes(field)
                ? ['projectName']
                : item?.dependentFieldsToReset,
              isRequired:
                field === 'projectName'
                  ? true
                  : item?.isRequired,
              requiredOnMainApply:
                field === 'projectName'
                  ? true
                  : item?.requiredOnMainApply,
              requiredOnMainApplyWhenFieldsActive:
                field === 'projectName'
                  ? ['projectVertical', 'projectType', 'projectStatus', 'dateRange']
                  : item?.requiredOnMainApplyWhenFieldsActive,
              requiredMessage:
                field === 'projectName'
                  ? 'Select Project Name before applying filters.'
                  : item?.requiredMessage,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                ...(field === 'dateRange'
                  ? {
                    maxRangeDays: 32,
                    maxRangeValidationMessage: DATE_RANGE_LIMIT_ERROR,
                  }
                  : {}),
                ...(field === 'projectName'
                  ? {
                    requiredOnMainApply: true,
                    requiredOnMainApplyWhenFieldsActive: ['projectVertical', 'projectType', 'projectStatus'],
                    requiredValidationMessage: 'Select Project Name before applying filters.',
                  }
                  : {}),
              },
            };
          })
          : menuGroup?.items,
      }))
      : buildMenuItems({
        field,
        header: getColumnLabel(column),
        template: field === 'dateRange'
          ? 'dateRange'
          : column?.filterTemplate || (getColumnNumeric(column) ? 'numberFilter' : 'multiSelect'),
        numeric: getColumnNumeric(column),
        filterUrl: column?.filterUrl,
        filterOptions: resolvedFilterOptions,
        sortable: column?.sortable ?? true,
        isGlobalFilter: column?.isGlobalFilter ?? false,
      });

    return {
      ...column,
      field,
      header: getRenderedHeader(column),
      sortable: column?.sortable ?? true,
      filter: column?.filter ?? false,
      visible: column?.visible ?? true,
      defaultPinned: resolvedDefaultPinned,
      isPinned: isEmployeeNameColumn ? false : false,
      pinnable: resolvedPinnable,
      pinVisible: resolvedPinVisible,
      filterVisible: column?.filterVisible,
      showPinButtonInHeader: resolvedShowPinButtonInHeader,
      showFilterButtonInHeader: column?.showFilterButtonInHeader,
      order: column?.order ?? index + 1,
      filterUrl: resolvedFilterOptions.length
        ? ''
        : column?.filterUrl,
      width: getForcedColumnWidth(field, column?.width, getColumnWidth(column, index)),
      bodyClassName: [
        getColumnClassName(field),
        column?.isHoliday ? 'employee-utilization-col-holiday' : '',
        getColumnNumeric(column) ? 'employee-utilization-report-numeric-cell' : '',
      ].filter(Boolean).join(' '),
      headerClassName: [
        getColumnClassName(field),
        isDayColumn(column) ? 'employee-utilization-col-day-header' : '',
        column?.isHoliday ? 'employee-utilization-col-holiday' : '',
        getColumnNumeric(column) ? 'employee-utilization-report-numeric-cell' : '',
      ].filter(Boolean).join(' '),
      className: [
        getColumnClassName(field),
        column?.isHoliday ? 'employee-utilization-col-holiday' : '',
        getColumnNumeric(column) ? 'employee-utilization-report-numeric-cell' : '',
      ].filter(Boolean).join(' '),
      isGlobalFilter: column?.isGlobalFilter ?? false,
      menuItems: resolvedMenuItems,
    };
  });

const DAY_FIELD_PATTERN = /^day_(\d{4})_(\d{2})_(\d{2})$/;

const getBackendTotalForField = (field = '', totals = {}) => {
  switch (field) {
  case 'workingDays':
    return totals?.workingDays ?? 0;
  case 'leaveDays':
    return totals?.leaveDays ?? 0;
  case 'expectedEffort':
    return totals?.expectedEfforts ?? 0;
  case 'totalLogged':
    return totals?.loggedEfforts ?? 0;
  case 'unloggedEffort':
    return totals?.unloggedEfforts ?? 0;
  case 'weekendHoliday':
    return totals?.weekendHolidayEfforts ?? 0;
  default: {
    const dayFieldMatch = String(field).match(DAY_FIELD_PATTERN);
    if (dayFieldMatch) {
      const [, year, month, day] = dayFieldMatch;
      const dateKey = `${year}-${month}-${day}`;
      return totals?.dailyTotals?.[dateKey] ?? 0;
    }

    return totals?.[field] ?? 0;
  }
  }
};

const buildTotalsRowFromBackend = (visibleColumns = [], totals = {}) => visibleColumns.reduce((acc, column, index) => {
  const field = getColumnField(column);

  if (!field) return acc;

  if (index === 0) {
    acc.id = 'total-row';
    acc[field] = 'Total';
    return acc;
  }

  if (!getColumnNumeric(column)) {
    acc[field] = '';
    return acc;
  }

  if (field === 'workingDays') {
    acc[field] = '';
    return acc;
  }

  acc[field] = formatMetricValue(parseNumericValue(getBackendTotalForField(field, totals)) ?? 0);
  return acc;
}, {
  rowConfiguration: {
    backgroundColor: '#eceff5',
    template: visibleColumns.map((column) => ({
      field: getColumnField(column),
      textColor: '#434956',
      fontWeight: '700',
    })),
  },
});

const buildTotalsRow = (visibleColumns = [], rows = []) => visibleColumns.reduce((acc, column, index) => {
  const field = getColumnField(column);

  if (!field) return acc;

  if (index === 0) {
    acc.id = 'total-row';
    acc[field] = 'Total';
    return acc;
  }

  if (!getColumnNumeric(column)) {
    acc[field] = '';
    return acc;
  }

  const total = rows.reduce((sum, row) => {
    const numericValue = parseNumericValue(row?.[field]);
    return sum + (numericValue || 0);
  }, 0);

  acc[field] = formatMetricValue(total);
  return acc;
}, {
  rowConfiguration: {
    backgroundColor: '#eceff5',
    template: visibleColumns.map((column) => ({
      field: getColumnField(column),
      textColor: '#434956',
      fontWeight: '700',
    })),
  },
});

export const EmployeeUtilizationReportTable = memo(({
  appliedFilters,
  initialDateRange,
  onRefresh,
}) => {
  const defaultAppliedFilterPayload = useMemo(
    () => buildDefaultAppliedFilterPayload(initialDateRange),
    [initialDateRange],
  );
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState(getInitialTableScrollHeight);
  const appliedFilterPayloadRef = useRef(defaultAppliedFilterPayload);
  const pendingToolbarFilterPayloadRef = useRef(cloneFilterPayload(defaultAppliedFilterPayload));
  const additionalPayloadDataRef = useRef(getPayloadWithDateRangeFields(
    buildAdditionalPayloadData(defaultAppliedFilterPayload),
  ));
  const lastResponseRef = useRef(null);
  const holidayDatesRef = useRef(new Set());
  const [toolbarHeading, setToolbarHeading] = useState(() => getReportHeading(appliedFilters));
  const [holidayMetadataVersion, setHolidayMetadataVersion] = useState(0);
  const [holidayDateRange, setHolidayDateRange] = useState(
    () => getInitialDateRangeFilter(initialDateRange),
  );
  const latestAppliedProjectRef = useRef(null);
  const activeProjectIdRef = useRef(null);
  const activeTableConfigurationRef = useRef([]);
  const activeTableRowsRef = useRef([]);
  const globalFilterOptionsRef = useRef({});
  const authToken = useMemo(
    () => (typeof window !== 'undefined' ? window.localStorage.getItem('authToken') || '' : ''),
    [],
  );
  const utilityUserInfo = useMemo(() => ({
    emailId: userProfile?.email,
    permissions: userProfile?.permissions,
    roleDetails: userProfile?.roleDetails,
    employeeId: userProfile?.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);
  const utilityUserInfoRef = useRef(utilityUserInfo);
  const effectiveAuthToken = userProfile?.access_token || authToken;
  useEffect(() => {
    Object.assign(utilityUserInfoRef.current, utilityUserInfo);
  }, [utilityUserInfo]);
  useEffect(() => {
    const [startDate, endDate] = Array.isArray(holidayDateRange) ? holidayDateRange : [];

    if (!startDate || !endDate) {
      holidayDatesRef.current = new Set();
      if (lastResponseRef.current) {
        lastResponseRef.current.data = {
          ...lastResponseRef.current.data,
          tableConfiguration: decorateHolidayColumns(
            lastResponseRef.current.data?.tableConfiguration,
            holidayDatesRef.current,
          ),
          data: {
            ...lastResponseRef.current.data?.data,
            tableConfiguration: decorateHolidayColumns(
              lastResponseRef.current.data?.data?.tableConfiguration,
              holidayDatesRef.current,
            ),
          },
        };
        setHolidayMetadataVersion((version) => version + 1);
      }
      return undefined;
    }

    let isActive = true;
    const abortController = new AbortController();

    const loadHolidayDates = async () => {
      try {
        const holidayRequest = getActivityReportHolidayListRequest({
          startDate,
          endDate,
          token: effectiveAuthToken,
        });

        const response = await fetch(holidayRequest.url, {
          method: holidayRequest.method,
          headers: holidayRequest.headers,
          signal: abortController.signal,
        });

        const responseBody = await response.json();
        const holidayRows = Array.isArray(responseBody?.holidayDetails)
          ? responseBody.holidayDetails
          : Array.isArray(responseBody?.data?.holidayDetails)
            ? responseBody.data.holidayDetails
            : Array.isArray(responseBody?.data)
              ? responseBody.data
              : [];
        const nextHolidayDates = new Set(
          holidayRows
            .map((holiday) => normalizeHolidayDate(
              holiday?.holidayDate || holiday?.holiday_date || holiday?.date,
            ))
            .filter(Boolean),
        );

        if (!isActive) {
          return;
        }

        holidayDatesRef.current = nextHolidayDates;

        if (lastResponseRef.current) {
          lastResponseRef.current.data = {
            ...lastResponseRef.current.data,
            tableConfiguration: decorateHolidayColumns(
              lastResponseRef.current.data?.tableConfiguration,
              holidayDatesRef.current,
            ),
            data: {
              ...lastResponseRef.current.data?.data,
              tableConfiguration: decorateHolidayColumns(
                lastResponseRef.current.data?.data?.tableConfiguration,
                holidayDatesRef.current,
              ),
            },
          };
          setHolidayMetadataVersion((version) => version + 1);
        }
      } catch {
        if (!isActive) {
          return;
        }
        holidayDatesRef.current = new Set();
      }
    };

    (async () => {
      await loadHolidayDates();
    })();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [holidayDateRange, effectiveAuthToken]);
  const toolbarAdditionalElements = useMemo(() => ([
    <EmployeeUtilizationMoreActions key="employee-utilization-more-actions" />,
  ]), []);
  const initialTableConfig = useMemo(() => {
    const filterOptionsByField = {
      ...globalFilterOptionsRef.current,
    };
    const resolvedColumns = buildResolvedColumns([], filterOptionsByField);
    const defaultDateRange = getInitialDateRangeFilter(initialDateRange);
    const resolvedColumnsWithHolidayFlags = decorateHolidayColumns(
      resolvedColumns,
      holidayDatesRef.current,
    );

    return buildDisplayColumns(resolvedColumnsWithHolidayFlags, filterOptionsByField)
      .map((column) => normalizeWeekendDayColumnLabel(column))
      .map((column) => {
        if (getColumnField(column) !== 'dateRange') {
          return column;
        }

        return {
          ...column,
          menuItems: buildMenuItems({
            field: 'dateRange',
            header: getColumnLabel(column),
            template: 'dateRange',
            numeric: false,
            sortable: false,
            isGlobalFilter: true,
            initialRange: defaultDateRange,
          }),
        };
      });
  }, [holidayMetadataVersion, initialDateRange]);

  useEffect(() => {
    activeTableConfigurationRef.current = initialTableConfig;
  }, [initialTableConfig]);

  const syncAdditionalPayloadDataRef = useCallback((filterPayload = {}, selectedProjectOverride) => {
    const hasExplicitProjectNameField = Object.prototype.hasOwnProperty.call(
      filterPayload || {},
      'projectName',
    );
    const selectedProjectFromPayload = getSelectedProjectFilterValue(filterPayload?.projectName);
    const selectedProjectValue = selectedProjectOverride
      || (hasExplicitProjectNameField ? selectedProjectFromPayload : null)
      || (!hasExplicitProjectNameField ? latestAppliedProjectRef.current : null)
      || null;
    const requestFilterPayload = {
      ...(filterPayload || {}),
      projectName: {
        ...(filterPayload?.projectName || {}),
        selectedValue: selectedProjectValue,
        selectedValues: selectedProjectValue ? [selectedProjectValue] : [],
      },
    };
    const payload = buildAdditionalPayloadData(requestFilterPayload);
    const nextPayload = getPayloadWithDateRangeFields({
      ...payload,
      projectName: {
        ...(payload?.projectName || {}),
        selectedValue: selectedProjectValue,
        selectedValues: selectedProjectValue ? [selectedProjectValue] : [],
      },
    });
    Object.keys(additionalPayloadDataRef.current).forEach((key) => {
      delete additionalPayloadDataRef.current[key];
    });
    Object.assign(additionalPayloadDataRef.current, {
      ...nextPayload,
    });
    setHolidayDateRange((previousRange) => (
      areDateRangesEqual(previousRange, nextPayload?.dateRange?.range)
        ? previousRange
        : nextPayload?.dateRange?.range
    ));

    return additionalPayloadDataRef.current;
  }, []);
  const maxFrozenColumns = useMemo(() => {
    return {
      desktop: 1,
      tablet: 1,
      mobile: 1,
    };
  }, []);

  const openProjectDetailsInNewTab = useCallback((projectId) => {
    if (projectId === null || projectId === undefined || `${projectId}`.trim() === '') {
      return;
    }

    window.open(buildProjectDetailsUrl(projectId), '_blank', 'noopener,noreferrer');
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    lastResponseRef.current = response;
    const currentTableConfiguration = activeTableConfigurationRef.current;
    const currentGlobalFilterOptions = globalFilterOptionsRef.current;
    const payload = getResponsePayload(response);
    const responseMeta = getResponseMeta(response, payload);
    const requestPayload = parseRequestPayload(response);
    const requestFilterPayload = buildFilterPayloadFromRequest(requestPayload);
    const rawColumns = Array.isArray(payload?.tableConfiguration)
      ? payload.tableConfiguration
      : Array.isArray(response?.data?.tableConfiguration)
        ? response.data.tableConfiguration
        : [];
    const rawRows = Array.isArray(payload?.tableData)
      ? payload.tableData
      : Array.isArray(response?.data?.tableData)
        ? response.data.tableData
        : [];
    const employeeNameOptions = buildEmployeeFilterOptions(rawRows);
    const responseDateRange = [
      payload?.dateRange?.startDate || null,
      payload?.dateRange?.endDate || null,
    ];
    const activeDateRangeFilter = getFilterItemByField(currentTableConfiguration, 'dateRange');
    const activeProjectNameFilter = getFilterItemByField(currentTableConfiguration, 'projectName');
    const requestedProjectValue = getSelectedProjectFilterValue(requestFilterPayload?.projectName);
    const requestProjectName = getSelectedProjectName(requestedProjectValue);
    const responseProjectName = String(payload?.project?.projectName || '').trim();
    const responseProjectId = payload?.project?.projectId;
    if (responseProjectId !== undefined && responseProjectId !== null && `${responseProjectId}`.trim() !== '') {
      activeProjectIdRef.current = responseProjectId;
    }
    const fallbackProjectSelection = latestAppliedProjectRef.current
      || getSelectedProjectFilterValue(activeProjectNameFilter);
    const activeProjectNameValue = requestedProjectValue
      ? requestedProjectValue
      : responseProjectName
        ? {
          id: responseProjectId ?? responseProjectName,
          name: responseProjectName,
          label: responseProjectName,
          value: responseProjectName,
        }
        : fallbackProjectSelection;
    const requestedDateRange = getNormalizedDateRange(requestFilterPayload);
    const configuredDateRange = Array.isArray(activeDateRangeFilter?.range)
      ? activeDateRangeFilter.range
      : [];
    const activeDateRange = hasCompleteDateRange(requestedDateRange)
      ? requestedDateRange
      : hasCompleteDateRange(responseDateRange)
        ? responseDateRange
        : configuredDateRange;
    const resolvedColumns = buildResolvedColumns(rawColumns, {
      ...currentGlobalFilterOptions,
      employeeName: mergeSingleSelectOptions(
        currentGlobalFilterOptions.employeeName,
        employeeNameOptions,
      ),
    });
    const resolvedColumnsWithHolidayFlags = decorateHolidayColumns(
      resolvedColumns,
      holidayDatesRef.current,
    );
    const displayColumns = syncDayColumnLabelsToRange(buildDisplayColumns(
      resolvedColumnsWithHolidayFlags,
      {
        ...currentGlobalFilterOptions,
        employeeName: mergeSingleSelectOptions(
          currentGlobalFilterOptions.employeeName,
          employeeNameOptions,
        ),
      },
    ), activeDateRange).map((column) => normalizeWeekendDayColumnLabel(column));
    const displayColumnsWithDateRange = activeDateRange.some((value) => value !== null && value !== undefined && `${value}`.trim())
      ? displayColumns.map((column) => {
        if (getColumnField(column) !== 'dateRange') {
          return column;
        }

        return {
          ...column,
          menuItems: Array.isArray(column?.menuItems)
            ? column.menuItems.map((menuGroup) => ({
              ...menuGroup,
              items: Array.isArray(menuGroup?.items)
                ? menuGroup.items.map((item) => (
                  item?.type === 'filter' && item?.field === 'dateRange'
                    ? {
                      ...item,
                      range: [...activeDateRange],
                    }
                    : item
                )).filter((item) => !(item?.type === 'sort' && getColumnField(column) === 'dateRange'))
                : menuGroup?.items,
            }))
            : column?.menuItems,
        };
      })
      : displayColumns;
    const displayColumnsWithActiveFilters = displayColumnsWithDateRange.map((column) => {
      if (getColumnField(column) !== 'projectName') {
        return column;
      }

      return {
        ...column,
        menuItems: Array.isArray(column?.menuItems)
          ? column.menuItems.map((menuGroup) => ({
            ...menuGroup,
            items: Array.isArray(menuGroup?.items)
              ? menuGroup.items.map((item) => (
                item?.type === 'filter' && item?.field === 'projectName'
                  ? {
                    ...item,
                    values: undefined,
                    options: undefined,
                    selectedValue: activeProjectNameValue || item?.selectedValue || null,
                  }
                  : item
              ))
              : menuGroup?.items,
          }))
          : column?.menuItems,
      };
    });
    const displayColumnsWithHolidayFlags = decorateHolidayColumns(
      displayColumnsWithActiveFilters,
      holidayDatesRef.current,
    );
    mergeFilterPayloadIntoTableConfig(
      displayColumnsWithHolidayFlags,
      requestFilterPayload,
    );
    const visibleColumns = displayColumnsWithHolidayFlags.filter((column) => column?.visible !== false);
    const normalizedRows = normalizeNumericDisplayRows(displayColumnsWithHolidayFlags, rawRows);
    const totalsRow = payload?.totals
      ? buildTotalsRowFromBackend(visibleColumns, payload.totals)
      : buildTotalsRow(visibleColumns, normalizedRows);
    const nextTableData = [...normalizedRows, totalsRow];
    activeTableRowsRef.current = nextTableData;
    const backendTotal = Number(
      responseMeta?.total
      ?? responseMeta?.count,
    );
    const resolvedTotal = Number.isFinite(backendTotal) ? backendTotal : normalizedRows.length;
    const resolvedPage = toPositiveInteger(
      responseMeta?.page,
      toPositiveInteger(requestPayload?.pagination?.page, 1),
    );
    const resolvedLimit = toPositiveInteger(
      responseMeta?.limit,
      toPositiveInteger(requestPayload?.pagination?.limit, PAGE_SIZE),
    );
    const nextAppliedFilterPayload = mergeFilterPayloads(
      buildCurrentFilterPayload(displayColumnsWithHolidayFlags),
      requestFilterPayload,
    );
    if (!getSelectedProjectFilterValue(nextAppliedFilterPayload?.projectName) && (requestProjectName || responseProjectName)) {
      const resolvedProjectName = requestProjectName || responseProjectName;
      nextAppliedFilterPayload.projectName = {
        selectedValue: {
          id: responseProjectId ?? resolvedProjectName,
          value: resolvedProjectName,
          label: resolvedProjectName,
          name: resolvedProjectName,
        },
      };
    }
    const projectNameFilterItem = getFilterItemByField(displayColumnsWithHolidayFlags, 'projectName');
    latestAppliedProjectRef.current = getSelectedProjectFilterValue(projectNameFilterItem) || null;
    pendingToolbarFilterPayloadRef.current = cloneFilterPayload(nextAppliedFilterPayload);
    appliedFilterPayloadRef.current = nextAppliedFilterPayload;
    syncAdditionalPayloadDataRef(nextAppliedFilterPayload, latestAppliedProjectRef.current);
    activeTableConfigurationRef.current = displayColumnsWithHolidayFlags;
    setToolbarHeading((previousHeading) => {
      const nextHeading = getReportTitleFromConfiguration(response, displayColumnsWithHolidayFlags);
      return previousHeading === nextHeading ? previousHeading : nextHeading;
    });

    response.data = {
      ...payload,
      tableConfiguration: displayColumnsWithHolidayFlags,
      tableData: nextTableData,
      data: {
        ...(payload?.data || {}),
        tableConfiguration: displayColumnsWithHolidayFlags,
        tableData: nextTableData,
      },
    };
    response.meta = {
      ...(responseMeta || {}),
      page: resolvedPage,
      limit: resolvedLimit,
      total: resolvedTotal,
    };
  }, [syncAdditionalPayloadDataRef]);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL: REPORT_BASE_URL,
        userInfo: utilityUserInfoRef.current,
        showPagination: false,
        showToolBar: true,
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: (context) => buildExportFileName(context),
            sheetName: (context) => buildExportSheetName(context),
            reportTitle: (context) => buildExportTitle(context),
          },
        },
        handler: {
          handleFilteredApiResponse,
        },
        additionalPayloadData: additionalPayloadDataRef.current,
        headers: {
          'Content-Type': 'application/json',
          token: effectiveAuthToken,
        },
        tableUtilityConfig: {
          isModifiedFilter: false,
        },
        toolBarSettings: {
          showFilterOption: true,
          showColumnFilterMenu: true,
          showAddNew: false,
          showDownloadExcel: true,
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showToolBarHeading: true,
          toolBarHeading: getReportHeading(appliedFilters),
          showSaveView: false,
          showReset: false,
          showExpandCollapse: false,
          additionalElements: toolbarAdditionalElements,
        },
      },
      tableSettings: {
        size: 'small',
        pageSize: PAGE_SIZE,
        paginationConfig: {
          page: 1,
          limit: PAGE_SIZE,
          rowsPerPageOptions: [10, 25, 50],
        },
        // Keep the prop-level table config stable. Filtered responses still inject the
        // latest tableConfiguration into TableUtility, but avoiding a new tableConfig
        // prop on every response prevents a second full reload cycle.
        tableConfig: initialTableConfig,
        loaderSkeleton: {
          rows: 10,
          bodyCells: 2,
          columns: 12,
        },
        showExpandCollapse: false,
        enableTreeTable: false,
        tableScrollHeight,
        maxFrozenColumns,
        holidayMetadataVersion,
      },
    }),
    [
      effectiveAuthToken,
      tableScrollHeight,
      maxFrozenColumns,
      holidayMetadataVersion,
      onRefresh,
      appliedFilters,
      handleFilteredApiResponse,
      initialTableConfig,
      toolbarAdditionalElements,
    ],
  );

  useEffect(() => {
    const rootNode = containerRef.current;
    if (!rootNode) {
      return;
    }

    const selectedProjectValue = getSelectedProjectFilterValue(
      appliedFilterPayloadRef.current?.projectName,
    ) || latestAppliedProjectRef.current;
    const selectedProjectName = getSelectedProjectName(selectedProjectValue);
    const selectedProjectId = selectedProjectValue?.id
      || selectedProjectValue?.value
      || activeProjectIdRef.current;
    const isProjectHeadingClickable = Boolean(selectedProjectName && selectedProjectId);

    const headingElements = rootNode.querySelectorAll(
      '.project-overview-header .toolbar-heading, .employee-utilization-header .toolbar-heading',
    );

    const cleanupCallbacks = [];

    headingElements.forEach((headingElement) => {
      if (isProjectHeadingClickable) {
        const projectHeadingPrefix = 'Employee Utilization Report - ';

        if (toolbarHeading.startsWith(projectHeadingPrefix)) {
          const projectNameOnly = toolbarHeading.slice(projectHeadingPrefix.length);
          headingElement.replaceChildren();

          const prefixNode = document.createTextNode(projectHeadingPrefix);
          const projectNameNode = document.createElement('span');
          projectNameNode.className = 'employee-utilization-heading-project-link';
          projectNameNode.textContent = projectNameOnly;
          projectNameNode.style.cursor = 'pointer';

          headingElement.append(prefixNode, projectNameNode);
        } else if (headingElement.textContent !== toolbarHeading) {
          headingElement.textContent = toolbarHeading;
        }

        headingElement.style.cursor = '';
      } else {
        if (headingElement.textContent !== toolbarHeading) {
          headingElement.textContent = toolbarHeading;
        }

        headingElement.style.cursor = '';
      }
    });

    return () => {
      cleanupCallbacks.forEach((callback) => callback());
    };
  }, [toolbarHeading, openProjectDetailsInNewTab]);

  useEffect(() => {
    const rootNode = containerRef.current;
    let animationFrameId = null;
    let repaintTimerId = null;

    const handleResetClick = (event) => {
      if (!rootNode) return;

      const clickedProjectName = event.target.closest(
        '.employee-utilization-heading-project-link',
      );
      if (clickedProjectName && rootNode.contains(clickedProjectName)) {
        const selectedProjectValue = getSelectedProjectFilterValue(
          appliedFilterPayloadRef.current?.projectName,
        ) || latestAppliedProjectRef.current;
        const selectedProjectId = selectedProjectValue?.id
          || selectedProjectValue?.value
          || activeProjectIdRef.current;

        openProjectDetailsInNewTab(selectedProjectId);
        return;
      }

      const resetButton = event.target.closest('button');
      if (!resetButton || !rootNode.contains(resetButton)) return;

      const clickedResetIcon
        = Boolean(event.target.closest('.pi-refresh'))
          || Boolean(resetButton.querySelector('.pi-refresh'));

      if (!clickedResetIcon) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onRefresh?.();
    };

    const updateTableClasses = () => {
      const activeColumns = activeTableConfigurationRef.current;
      applyEmployeeTableViewport(rootNode);
      applyEmployeeUtilizationHeaderClass(rootNode);
      applyEmployeeDayHeaders(rootNode, activeColumns);
      applyEmployeeUtilizationIconClass();
      applyBodyCellHoverTitles();
      applyToolbarButtonClasses(rootNode);
      applyNumericColumnAlignment(rootNode, activeColumns);
      applyForcedColumnWidths(rootNode, activeColumns);
      applyDailyEffortCellStyles(rootNode, activeTableRowsRef.current, activeColumns);
      if (repaintTimerId !== null) {
        window.clearTimeout(repaintTimerId);
      }
      repaintTimerId = window.setTimeout(() => {
        repaintTimerId = null;
        applyDailyEffortCellStyles(rootNode, activeTableRowsRef.current, activeTableConfigurationRef.current);
      }, 120);
    };

    const scheduleTableClassUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        updateTableClasses();
      });
    };

    rootNode?.addEventListener('click', handleResetClick, true);

    const timer = setTimeout(scheduleTableClassUpdate, 500);

    const tableContainer = document.querySelector('.employee-utilization-table-utility');
    const observer = new MutationObserver(scheduleTableClassUpdate);

    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const handleResize = () => {
      const nextTableScrollHeight = getInitialTableScrollHeight();
      setTableScrollHeight((previousHeight) => (
        previousHeight === nextTableScrollHeight ? previousHeight : nextTableScrollHeight
      ));
    };

    window.addEventListener('resize', handleResize);

    return () => {
      rootNode?.removeEventListener('click', handleResetClick, true);
      clearTimeout(timer);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (repaintTimerId !== null) {
        window.clearTimeout(repaintTimerId);
      }
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [holidayMetadataVersion, onRefresh, openProjectDetailsInNewTab]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const setPendingMultiSelectField = (field, selectedLabels = []) => {
      if (!field) {
        return;
      }

      const sourceFilterItem = getFilterItemByField(activeTableConfigurationRef.current, field);
      const availableOptions = mergeSingleSelectOptions(
        sourceFilterItem?.values,
        sourceFilterItem?.options,
        sourceFilterItem?.selectedValues,
      );
      const normalizedSelectedLabels = selectedLabels.map((label) => (
        String(label || '')
          .trim()
          .toLowerCase()
      ));
      const matchedOptions = availableOptions.filter((option) => {
        const optionLabels = [
          option?.label,
          option?.name,
          option?.value,
          option?.id,
        ]
          .filter((value) => value !== undefined && value !== null)
          .map((value) => `${value}`.trim().toLowerCase());

        return optionLabels.some((optionLabel) => normalizedSelectedLabels.includes(optionLabel));
      });

      const nextPendingPayload = {
        ...cloneFilterPayload(pendingToolbarFilterPayloadRef.current),
        [field]: {
          selectedValues: matchedOptions.length
            ? matchedOptions
            : selectedLabels.map((label) => ({
              id: label,
              name: label,
              label,
              value: label,
            })),
        },
      };
      pendingToolbarFilterPayloadRef.current = PROJECT_NAME_DEPENDENCY_FIELDS.includes(field)
        ? clearProjectNameFromFilterPayload(nextPendingPayload)
        : nextPendingPayload;
      if (PROJECT_NAME_DEPENDENCY_FIELDS.includes(field)) {
        latestAppliedProjectRef.current = null;
      }
      syncAdditionalPayloadDataRef(pendingToolbarFilterPayloadRef.current);
    };

    const handleToolbarFilterClick = (event) => {
      const singleSelectApplyButton = event.target.closest('.tu-single-select-apply');
      if (singleSelectApplyButton) {
        const filterElement = singleSelectApplyButton.closest('.tu-single-select-filter');
        const titleText = filterElement?.querySelector('.tu-single-select-title')?.textContent || '';
        if (String(titleText)
          .toLowerCase()
          .includes('project name')) {
          const selectedProjectName = String(
            filterElement?.querySelector('.tu-single-select-input')?.value || '',
          ).trim();

          pendingToolbarFilterPayloadRef.current = {
            ...cloneFilterPayload(pendingToolbarFilterPayloadRef.current),
            projectName: selectedProjectName
              ? {
                selectedValue: {
                  id: selectedProjectName,
                  name: selectedProjectName,
                  label: selectedProjectName,
                  value: selectedProjectName,
                },
                selectedValues: [{
                  id: selectedProjectName,
                  name: selectedProjectName,
                  label: selectedProjectName,
                  value: selectedProjectName,
                }],
              }
              : {
                selectedValue: null,
                selectedValues: [],
              },
          };
          syncAdditionalPayloadDataRef(pendingToolbarFilterPayloadRef.current);
        }
        return;
      }

      const singleSelectResetButton = event.target.closest('.tu-single-select-reset');
      if (singleSelectResetButton) {
        const filterElement = singleSelectResetButton.closest('.tu-single-select-filter');
        const titleText = filterElement?.querySelector('.tu-single-select-title')?.textContent || '';
        if (String(titleText)
          .toLowerCase()
          .includes('project name')) {
          pendingToolbarFilterPayloadRef.current = {
            ...cloneFilterPayload(pendingToolbarFilterPayloadRef.current),
            projectName: {
              selectedValue: null,
              selectedValues: [],
            },
          };
          syncAdditionalPayloadDataRef(pendingToolbarFilterPayloadRef.current);
        }
        return;
      }

      const multiSelectApplyButton = event.target.closest('.tu-multi-select-apply');
      if (multiSelectApplyButton) {
        const filterElement = multiSelectApplyButton.closest('.tu-multi-select-filter');
        const titleElement = filterElement?.querySelector(
          '.tu-multi-select-title, .tu-filter-template-title, .tu-filter-title',
        );
        const field = getFieldFromVisibleMultiSelectTitle(titleElement?.textContent || '');
        const selectedLabels = getVisibleMultiSelectLabels(filterElement);
        window.setTimeout(() => {
          setPendingMultiSelectField(field, selectedLabels);
        }, 0);
        return;
      }

      const multiSelectResetButton = event.target.closest('.tu-multi-select-reset');
      if (multiSelectResetButton) {
        const filterElement = multiSelectResetButton.closest('.tu-multi-select-filter');
        const titleElement = filterElement?.querySelector(
          '.tu-multi-select-title, .tu-filter-template-title, .tu-filter-title',
        );
        const field = getFieldFromVisibleMultiSelectTitle(titleElement?.textContent || '');
        if (field) {
          window.setTimeout(() => {
            const nextPendingPayload = {
              ...cloneFilterPayload(pendingToolbarFilterPayloadRef.current),
              [field]: {
                selectedValues: [],
              },
            };
            pendingToolbarFilterPayloadRef.current = PROJECT_NAME_DEPENDENCY_FIELDS.includes(field)
              ? clearProjectNameFromFilterPayload(nextPendingPayload)
              : nextPendingPayload;
            if (PROJECT_NAME_DEPENDENCY_FIELDS.includes(field)) {
              latestAppliedProjectRef.current = null;
            }
            syncAdditionalPayloadDataRef(pendingToolbarFilterPayloadRef.current);
          }, 0);
        }
        return;
      }

      const valueConditionApplyButton = event.target.closest(
        '.tu-value-condition-apply, .tu-outside-filter-apply',
      );
      if (valueConditionApplyButton) {
        const nextPendingPayload = cloneFilterPayload(pendingToolbarFilterPayloadRef.current);
        const visibleDateRange = getVisibleDateRangeFromDom(valueConditionApplyButton);
        if (hasCompleteDateRange(visibleDateRange)) {
          nextPendingPayload.dateRange = {
            ...nextPendingPayload.dateRange,
            range: [...visibleDateRange],
          };
        }
        pendingToolbarFilterPayloadRef.current = nextPendingPayload;
        syncAdditionalPayloadDataRef(pendingToolbarFilterPayloadRef.current);
        return;
      }

      const valueConditionResetButton = event.target.closest(
        '.tu-value-condition-reset, .tu-outside-filter-reset',
      );
      if (valueConditionResetButton) {
        const nextPendingPayload = cloneFilterPayload(pendingToolbarFilterPayloadRef.current);
        nextPendingPayload.dateRange = {
          ...nextPendingPayload.dateRange,
          range: [null, null],
        };
        pendingToolbarFilterPayloadRef.current = nextPendingPayload;
        syncAdditionalPayloadDataRef(pendingToolbarFilterPayloadRef.current);
      }
    };

    document.addEventListener('click', handleToolbarFilterClick, true);

    return () => {
      document.removeEventListener('click', handleToolbarFilterClick, true);
    };
  }, [syncAdditionalPayloadDataRef]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const syncValidation = (event) => {
      syncVisibleDateRangeValidation(event?.target || document);
    };

    const timer = window.setTimeout(() => {
      syncVisibleDateRangeValidation(document);
    }, 0);

    document.addEventListener('click', syncValidation, true);
    document.addEventListener('input', syncValidation, true);
    document.addEventListener('change', syncValidation, true);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('click', syncValidation, true);
      document.removeEventListener('input', syncValidation, true);
      document.removeEventListener('change', syncValidation, true);
    };
  }, []);

  if (!effectiveAuthToken) {
    return null;
  }

  return (
    <div className="employee-utilization-outer-layer" ref={containerRef}>
      <div className="employee-utilization-table-container">
        <div className="employee-utilization-report-legend" aria-label="Effort color legend">
          {DAILY_EFFORT_LEGEND_ITEMS.map((item) => (
            <span className="employee-utilization-report-legend-item" key={item.label}>
              <span
                className={`employee-utilization-report-legend-dot ${item.swatchClassName}`}
                aria-hidden="true"
              />
              <span>{item.label}</span>
            </span>
          ))}
        </div>
        <div className="employee-utilization-table-utility table-utility">
          <TableUtility
            configuration={configuration}
          />
        </div>
      </div>
    </div>
  );
});

EmployeeUtilizationReportTable.displayName = 'EmployeeUtilizationReportTable';

EmployeeUtilizationReportTable.propTypes = {
  appliedFilters: PropTypes.object,
  initialDateRange: PropTypes.object,
  onRefresh: PropTypes.func,
};

export default EmployeeUtilizationReportTable;
